import { useEffect, useState } from 'react'
import { RequiredFieldStateCode } from "../../../enums/EtatChampObligatoire"
import { SousBlocOption, TemplateFieldReferentiel } from "../../../types/ChampsObligatoires"

export interface SousBlocSelectOption {
    value: string
    label: string
}

export const AUCUN_SOUS_BLOC = '__aucun__'
export const AUCUN_SOUS_BLOC_OPTION: SousBlocSelectOption = { value: AUCUN_SOUS_BLOC, label: 'Aucun' }

interface EditDraft {
    label: string
    sousBloc: SousBlocSelectOption
}

interface TemplateFieldPayload {
    fieldId: number
    subBlockId: number | null
    type: string | null
    isCore: boolean
    defaultState: RequiredFieldStateCode
}

interface UseBlocAdminEditingParams {
    blocId: number
    adminMode: boolean
    onFetchSubBlocs: (blockId: number) => Promise<SousBlocOption[]>
    onCreateTemplateField: (payload: TemplateFieldPayload) => Promise<void>
    onUpdateTemplateFieldCore: (templateFieldId: number, isCore: boolean) => Promise<void>
    onUpdateTemplateFieldDetails: (templateFieldId: number, label: string, subBlockId: number | null) => Promise<void>
    onDeleteTemplateField: (templateFieldId: number) => Promise<void>
    reload: () => Promise<void>
}

/**
 * État et actions de l'édition admin d'un bloc : création/édition/suppression de
 * template_fields et de sous-blocs sur le référentiel (BDREF3). BlocCard reste seul
 * responsable de l'affichage "réel" (filtrage, regroupement, état par groupe) ; ce hook
 * ne fait que fournir ce que les composants admin (BlocAdminPanel, TemplateFieldAdminControls)
 * ont besoin d'afficher/déclencher.
 */
const useBlocAdminEditing = ({
    blocId, adminMode, onFetchSubBlocs, onCreateTemplateField, onUpdateTemplateFieldCore,
    onUpdateTemplateFieldDetails, onDeleteTemplateField, reload,
}: UseBlocAdminEditingParams) => {
    const [showAjoutForm, setShowAjoutForm] = useState(false)
    const [sousBlocsExistants, setSousBlocsExistants] = useState<SousBlocOption[]>([])
    const [editDrafts, setEditDrafts] = useState<Map<number, EditDraft>>(new Map())
    const [savingDetails, setSavingDetails] = useState(false)
    const [updatingCoreId, setUpdatingCoreId] = useState<number | null>(null)

    useEffect(() => {
        if (adminMode) {
            onFetchSubBlocs(blocId).then(setSousBlocsExistants).catch(console.log)
        }
    }, [adminMode, blocId])

    const sousBlocOptions: SousBlocSelectOption[] = [
        AUCUN_SOUS_BLOC_OPTION,
        ...sousBlocsExistants.map(sb => ({ value: String(sb.id), label: sb.label })),
    ]

    const handleToggleCore = async (templateFieldId: number, isCore: boolean) => {
        setUpdatingCoreId(templateFieldId)
        try {
            await onUpdateTemplateFieldCore(templateFieldId, isCore)
            await reload()
        } catch (error) {
            console.log(error)
        } finally {
            setUpdatingCoreId(null)
        }
    }

    const handleAjoutSubmit = async (payloads: TemplateFieldPayload[]) => {
        for (const payload of payloads) {
            await onCreateTemplateField(payload)
        }
        await reload()
        setSousBlocsExistants(await onFetchSubBlocs(blocId))
        setShowAjoutForm(false)
    }

    const handleStartEdit = (champ: TemplateFieldReferentiel) => {
        setEditDrafts(prev => {
            const next = new Map(prev)
            next.set(champ.templateFieldId, {
                label: champ.fieldLabel,
                sousBloc: champ.subBlockId != null
                    ? { value: String(champ.subBlockId), label: champ.subBlockLabel || '' }
                    : AUCUN_SOUS_BLOC_OPTION,
            })
            return next
        })
    }

    const handleUpdateDraft = (templateFieldId: number, patch: Partial<EditDraft>) => {
        setEditDrafts(prev => {
            const current = prev.get(templateFieldId)
            if (!current) return prev
            const next = new Map(prev)
            next.set(templateFieldId, { ...current, ...patch })
            return next
        })
    }

    const handleCancelEdit = (templateFieldId: number) => {
        setEditDrafts(prev => {
            const next = new Map(prev)
            next.delete(templateFieldId)
            return next
        })
    }

    const handleCancelAllEdits = () => setEditDrafts(new Map())

    const hasEmptyDraftLabel = Array.from(editDrafts.values()).some(d => !d.label.trim())

    const handleSaveAllEdits = async () => {
        if (editDrafts.size === 0 || hasEmptyDraftLabel) return

        setSavingDetails(true)
        try {
            for (const [templateFieldId, draft] of editDrafts) {
                const subBlockId = draft.sousBloc.value !== AUCUN_SOUS_BLOC ? Number(draft.sousBloc.value) : null
                await onUpdateTemplateFieldDetails(templateFieldId, draft.label.trim(), subBlockId)
            }
            await reload()
            setSousBlocsExistants(await onFetchSubBlocs(blocId))
            setEditDrafts(new Map())
        } catch (error) {
            console.log(error)
        } finally {
            setSavingDetails(false)
        }
    }

    const handleDelete = async (champ: TemplateFieldReferentiel) => {
        if (!window.confirm(`Retirer "${champ.fieldLabel}" de ce bloc ?`)) return
        await onDeleteTemplateField(champ.templateFieldId)
        await reload()
    }

    return {
        showAjoutForm, setShowAjoutForm,
        sousBlocsExistants, sousBlocOptions,
        handleAjoutSubmit,
        editDrafts, hasEmptyDraftLabel, savingDetails,
        handleStartEdit, handleUpdateDraft, handleCancelEdit, handleCancelAllEdits, handleSaveAllEdits,
        updatingCoreId, handleToggleCore, handleDelete,
    }
}

export default useBlocAdminEditing
